// Selectors for the BYO/YPX slot wizard (app/javascript/components/v3/
// productConfig). Separate from productConfig.ts, which covers the regular
// single-product config screen.
import { createSelector } from 'reselect';
import { productNeedsBackgroundSelection } from '../components/v3/products/productConfigPredicates';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Slot = any;

// The child products the customer has configured so far, in slot order.
//
// `v3Order.productConfig` is keyed by slot index and holds one entry per slot the
// customer has touched — `{ product, pose | poses, background, cropOptions,
// personalization }`. Slots with no product picked yet are skipped, so this is
// "how many items are configured", which the completion gates compare against
// their minimums.
//
// Memoized on the productConfig reference so the array identity is stable
// between renders; every consumer needs it as a list, and rebuilding it inline
// (as configScreen and slotSelector both did) produced a fresh array per render.
export const selectConfiguredChildProducts = createSelector(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (state: any) => state.v3Order?.productConfig,
  (productConfig): Slot[] =>
    Object.values(productConfig || {})
      .filter((slot: Slot) => slot?.product)
      .map((slot: Slot) => slot.product),
);

// The ordered step list the wizard walks. Every step after 'product' is
// conditional on store state, which is why this belongs here rather than being
// re-derived from `shoot` + `additionalData` at each call site — it used to be
// recomputed at nine sites across five files, each needing to know which two
// slices to read.
//
// Memoized on the three PRIMITIVES it depends on rather than on the slices: the
// result is a fresh array each time it recomputes, so keying on `state.shoot` or
// `state.additionalData` would hand useSelector a new array reference whenever
// anything unrelated in those slices changed and re-render every consumer. Keyed
// on booleans, the identity only changes when the flow actually changes.
export const selectConfigFlowPages = createSelector(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (state: any) => Boolean(state.shoot?.isPrepay),
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (state: any) => Boolean(state.shoot?.isGreenScreen),
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (state: any) => Boolean(state.additionalData?.isGeskus),
  (isPrepay, isGreenScreen, isGeskus): string[] => {
    const pages = ['product'];

    // No pose step when there are no photos to pick from: prepay shoots happen
    // before picture day, and Geskus galleries are preview-only.
    if (!isPrepay && !isGeskus) pages.push('pose');

    // Backgrounds are only selectable on green-screen shoots.
    if (isGreenScreen) pages.push('background');

    return pages;
  },
);

// Whether every configured child that takes a background choice has actually
// made one — the completion gate, read by configScreen and slotSelector.
//
// Asked PER SLOT, using the same question the navigation asks: will the wizard
// offer this child a background page at all? A child owes a background exactly
// when it is offered one, or the parent can never be completed — the gate
// asking for backgrounds the flow refuses to collect is the ENG-2630 bug, and it
// first appeared as one background per configured child, which no parent mixing
// skipping and non-skipping children could ever satisfy.
//
// Two things take a child out of the gate, and both must match the skip in
// getNextUrl (components/v3/productConfig/utils.js):
//
//   * a digital download that resolves its background automatically (none, or
//     all of them) — productSkipsBackgroundSelection;
//   * a child with no background slots of its own (`backgrounds_count` 0 or
//     absent) — the product has no background to composite, so a choice has
//     nowhere to go.
//
// Both live in productNeedsBackgroundSelection, so this reads that rather than
// re-deriving either half. The gate used to apply the first condition only,
// deliberately, because navigation offered a zero-slot non-DD child a background
// page anyway (ENG-2552). Note what that page did NOT do: Background.tsx renders
// `shoot.backgroundOptions` and never reads the child, so the customer saw the
// full catalogue and picked from it — the stray code that reached the lab, and
// that Order#missing_green_screen_images? mistook for a completed selection
// (ENG-2300). Navigation now skips those children too, and the two sides are
// pinned in agreement by configFlow.test.js.
//
// PER SLOT, not a count. The gate used to compare the number of backgrounds
// collected anywhere against the number of children offered a page, which never
// asked WHICH child each background belonged to. Two children, one of them a
// zero-slot child carrying a background left over from the product it replaced
// and the other a portrait child that has picked nothing: required 1, collected
// 1, gate passes — and the child that owes a background goes to the lab without
// one while the child that cannot use one ships with it. The count also had to
// be one per CHILD, not one per background slot, because the background step
// collects a single background per child however many slots it declares; the
// per-slot question here sidesteps that trap as well.
//
// A slot with no product owes nothing; the minimum-child-count gate is what
// covers an unconfigured slot. With no 'background' page at all (any
// non-green-screen shoot) nothing can be collected, so nothing is owed.
export const selectBackgroundsSatisfied = createSelector(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (state: any) => state.v3Order?.productConfig,
  selectConfigFlowPages,
  (productConfig, pages): boolean =>
    !pages.includes('background') ||
    Object.values(productConfig || {}).every(
      (slot: Slot) =>
        !productNeedsBackgroundSelection(slot?.product) ||
        Boolean(slot?.background),
    ),
);
